import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { CAN_OUTPUT, DIGITAL_OUTPUT, XMC } from 'utils/constants'
import strings from 'utils/strings'
import { zeroPad } from 'utils/utils'
import { Alert, Header, Paragraph } from 'components/patterns'
import ConfigurationTemplate from 'components/vehicles/Summary/ChecPlusVehicleSummary/AddRemoveVehicleFeatures/ConfigurationTemplate'
import {
  ConflictsHeader,
  FeatureConflictSubHeader,
  StyledHeader,
  ArticleStyled,
  ConflictResolveContainer,
} from './AddRemoveVehicleFeatures.style'

import {
  StyledRow,
  ConflictDetailsContainer,
  StyledContainerDiv,
  ConflictTableCol,
  ConflictDetailsCol,
} from './ConflictsResolveView.style'
import ConflictTable from './ConflictTable'
import colors from 'theme/color/defaults'

const ConflictsResolveView = ({
  conflicts,
  view,
  allFeatures,
  onSelect,
  currentConflictSelection,
  currentConflictStep,
  className,
  conflictMessages,
  statusLabels,
}) => {
  const [selectedOutputConfig, setSelectedOutputConfig] = useState(null)

  useEffect(() => {
    if (!currentConflictSelection) {
      setSelectedOutputConfig(null)
    }
  }, [currentConflictSelection])

  const header = {
    digitalInput: strings.digital_input_configuration_conflicts,
    analogInput: strings.analog_input_configuration_conflicts,
    digitalOutput: strings.digital_output_conflicts,
    canOutput: strings.can_output_conflicts,
  }

  const warning = {
    digitalInput: strings.features_conflict_digital_input,
    analogInput: strings.features_conflict_analog_input,
    digitalOutput: strings.features_conflict_digital_output,
    canOutput: strings.features_conflict_can_output,
    ...(conflictMessages || {}),
  }

  const conflictsRemainingMessage = {
    digitalInput: strings.remaining_digital_input_conflicts,
    analogInput: strings.remaining_analog_input_conflicts,
    digitalOutput: strings.remaining_digital_output_conflicts,
    canOutput: strings.remaining_can_output_conflicts,
  }

  const modulePinConflicts = Object.values(conflicts[view])
    .map((module) => {
      const pins = Object.values(module)
      return pins
    })
    .flat()
    .filter((pin) => pin.selection === null)

  // modulePinConflicts is already filtered to unresolved pins above.
  const conflictsRemaining = modulePinConflicts.length

  const renderConfigurationTemplate = () => {
    if (!selectedOutputConfig) {
      return (
        <StyledContainerDiv>
          <Paragraph size="small" color={colors.greyPrimary}>
            {strings.select_a_configuration}
          </Paragraph>
        </StyledContainerDiv>
      )
    }

    const { label } = selectedOutputConfig

    return (
      <>
        <Header type="h3">{label}</Header>
        <ConfigurationTemplate configDetails={selectedOutputConfig} />
      </>
    )
  }

  const renderTables = () => {
    const {
      module,
      pin,
      features,
      connector,
      location,
      outputConflicts,
      canFeatures,
      canId,
      numberOfBits,
      startBit,
    } = modulePinConflicts[currentConflictStep]

    const subHeader = {
      digitalInput: `${
        strings.select_which_configuration_digital_input
      } ${zeroPad(
        currentConflictStep + 1,
        2
      )}: ${module} ${connector}-${location}`,
      analogInput: `${
        strings.select_which_configuration_analog_input
      } ${zeroPad(
        currentConflictStep + 1,
        2
      )}: ${module} ${connector}-${location}`,
      digitalOutput: `${
        strings.select_which_configuration_digital_output
      } ${zeroPad(
        currentConflictStep + 1,
        2
      )}: ${module} ${connector}-${location}`,
      canOutput: strings.select_which_configuration_can_output({
        module: module.startsWith(XMC)
          ? `${module.substring(0, module.length - 1)} ${module.substring(
              module.length - 1
            )}`
          : module,
        canId,
        startBit,
        numberOfBits,
      }),
    }

    const handleOnSelect =
      (pinModule, modulePin, nextView, value) => (selection) => {
        setSelectedOutputConfig(value)
        onSelect(pinModule, modulePin, nextView, selection)
      }

    return (
      <>
        <FeatureConflictSubHeader type="h2">
          {subHeader[view]}
        </FeatureConflictSubHeader>
        {view !== CAN_OUTPUT &&
          (view !== DIGITAL_OUTPUT
            ? Object.entries(features).map(([key, value]) => (
                <ConflictTable
                  key={key}
                  option={key}
                  data={value}
                  view={view}
                  allFeatures={allFeatures}
                  selection={currentConflictSelection}
                  onSelect={handleOnSelect(module, pin || canId, view)}
                  statusLabels={statusLabels}
                />
              ))
            : Object.entries(outputConflicts).map(([key, value]) => {
                const { featureSet, label } = value
                return (
                  <ConflictTable
                    data={featureSet}
                    key={key}
                    view={view}
                    option={key}
                    label={label}
                    allFeatures={allFeatures}
                    selection={currentConflictSelection}
                    onSelect={handleOnSelect(module, pin, view, value)}
                    statusLabels={statusLabels}
                  />
                )
              }))}

        {view === CAN_OUTPUT && (
          <ConflictTable
            data={canFeatures}
            view={view}
            allFeatures={allFeatures}
            selection={currentConflictSelection}
            onSelect={handleOnSelect(module, canId, view)}
            statusLabels={statusLabels}
          />
        )}
      </>
    )
  }

  const isDigitalView = view === DIGITAL_OUTPUT
  const colSpan = !isDigitalView ? 24 : 16

  return (
    <>
      <ConflictsHeader>
        <StyledHeader>
          <div>{header[view]}</div>
          <span style={{ fontSize: 12, fontWeight: 'normal' }}>
            {conflictsRemainingMessage[view]}:{' '}
            <span>
              <strong>{conflictsRemaining}</strong>
            </span>
          </span>
        </StyledHeader>
      </ConflictsHeader>
      <ConflictResolveContainer className={className}>
        <StyledRow>
          <ConflictTableCol span={colSpan}>
            <ArticleStyled>
              <Alert
                type="warning"
                message={warning[view]}
                closable={false}
                persistent
              />
              {renderTables()}
            </ArticleStyled>
          </ConflictTableCol>
          {isDigitalView && (
            <ConflictDetailsCol span={8}>
              <ConflictDetailsContainer>
                {renderConfigurationTemplate()}
              </ConflictDetailsContainer>
            </ConflictDetailsCol>
          )}
        </StyledRow>
      </ConflictResolveContainer>
    </>
  )
}

ConflictsResolveView.propTypes = {
  conflicts: PropTypes.object,
  view: PropTypes.string,
  allFeatures: PropTypes.array,
  onSelect: PropTypes.func,
  currentConflictSelection: PropTypes.string,
  currentConflictStep: PropTypes.number,
  className: PropTypes.string,
  /** Optional override map for the warning text per conflict type.
   * Keys: digitalInput | analogInput | digitalOutput | canOutput */
  conflictMessages: PropTypes.object,
  /** Optional override for status column labels in ConflictTable.
   * Keys: onEntity | whatIsOnEntity | whatIsNewFeature */
  statusLabels: PropTypes.object,
}

export default ConflictsResolveView
